<template>
	<div
		class="modal fade"
		ref="modal"
		role="dialog"
		aria-hidden="true"
		:no-enforce-focus="true"
	>
		<div class="vld-parent modal-dialog" role="document">
			<loading :active.sync="loading" :is-full-page="false" />
			<div class="modal-content">
				<button
					type="button"
					class="modal-close dark-modal-close"
					data-dismiss="modal"
					aria-label="Close"
				>
					<i class="sk-icon-times"></i>
				</button>
				<div class="item-content sk-scrollbar-white" ref="itemContent">
					<div class="item-image" :style="bannerPath">
						<div
							class="selected-date-tag"
							v-if="showSelectedDateTag"
						>
							<b>{{ selectedDateDay }}</b>
							- {{ productMenuName }}
						</div>
					</div>
					<div class="panel">
						<div class="item-modal-body">
							<h2 class="item-modal-title">
								{{ item.name }}
								<span>{{ cost | currency }}</span>
							</h2>
							<h3 class="item-modal-desc">
								{{ item.description }}
							</h3>
							<AlcoholDisclaimer v-if="item.contains_alcohol" />
							<div v-if="metas">
								<div
									v-for="(meta, metaIndex) of metas"
									:key="meta.key"
									:class="{
										invalid: invalidMetaId == meta.id
									}"
									:ref="'meta-' + meta.id"
								>
									<h5 class="item-modal-choose">
										{{ meta.key }}
										<span
											class="item-option-required"
											v-if="meta.required"
										>
											Required
										</span>
									</h5>
									<ItemOption
										v-for="(option, index) in meta.values"
										:key="index"
										:meta="meta"
										:option="option"
										:metaIndex="metaIndex"
										:item="item"
										@change="optionChanged"
									/>
								</div>
							</div>
							<div v-if="showRequiresFoodDisclaimer">
								<h5 class="item-modal-choose">
									Alcohol orders require food items
								</h5>
								<h4 class="item-modal-subtitle">
									Consider adding one of these menu items to
									your order
								</h4>
								<div
									class="custom-control custom-radio"
									v-for="foodItem in foodItemSuggestions"
									:key="foodItem.id"
									@click="
										foodItemSuggestionSelected(foodItem)
									"
								>
									<input
										type="radio"
										class="custom-control-input"
										:value="foodItem.id"
										:checked="
											foodItem.id ==
											suggestedItemSelected.id
										"
									/>
									<label class="custom-control-label">
										{{ foodItem.name }}
										<span
											class="
												custom-control-label-description
											"
										>
											+ {{ foodItem.price | currency }}
										</span>
									</label>
								</div>
							</div>
							<button
								class="button button-white"
								type="button"
								@click="toggleInstructions"
								data-cy="add-comments"
								v-if="showComments"
							>
								Add Additional Comments
							</button>
							<transition
								name="collapse"
								v-on:after-enter="commentsOpened"
							>
								<div v-show="commentsOpen">
									<h5 class="item-modal-choose">
										Additional Comments
									</h5>
									<div class="form-group">
										<textarea
											class="form-control"
											placeholder="List any additional comments or preferences. We cannot guarantee the restaurant will honor extra instructions."
											@change="setComments"
											:value="item.comments"
											data-cy="comment-input"
										></textarea>
									</div>
								</div>
							</transition>
						</div>
					</div>
				</div>
				<ItemFooter
					:inStockMetas="metas"
					@invalidMeta="scrollToRequiredMeta"
					@addedToCart="close"
					@confirmClear="confirmClear"
				/>
			</div>
		</div>
		<ConfirmationModal ref="confirmationModal" />
	</div>
</template>

<script>
import { BusinessType } from "@reef-os/utils/enums/businessType"
import moment from "moment"
import momentUtils from "@/moment-utils"
import Loading from "vue-loading-overlay"
import currencyFilterMixin from "@/mixins/currency-filter-mixin"
import Navbar from "@/components/navbars/Navbar.vue"
import ItemFooter from "@/components/ItemFooter.vue"
import ItemOption from "@/components/ItemOption.vue"
import AlcoholDisclaimer from "@/components/common/AlcoholDisclaimer.vue"
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue"

export default {
	name: "ItemModal",
	mixins: [currencyFilterMixin],
	components: {
		Loading,
		Navbar,
		ItemFooter,
		ItemOption,
		AlcoholDisclaimer,
		ConfirmationModal
	},
	data: function () {
		return {
			invalidMetaId: null,
			commentsOpen: false,
			waitingToOpen: false
		}
	},
	computed: {
		loading() {
			return !this.restaurant || !this.item.id
		},
		item() {
			return this.$store.state.currentItem
		},
		restaurant() {
			if (!this.item) {
				return {}
			}
			return this.$store.state.restaurants[this.item.restaurant_id]
		},
		hasImage() {
			return (
				this.item &&
				this.item.img &&
				this.item.img != "" &&
				this.restaurant &&
				this.restaurant.slug &&
				this.restaurant.slug.length > 0
			)
		},
		bannerPath() {
			if (this.hasImage) {
				const filenameArr = this.item.img.split(".")
				const sizedFileName = `${filenameArr[0]}_500x500.${filenameArr[1]}`
				return {
					backgroundImage: `url('${process.env.VUE_APP_BASE_RESTAURANTS_IMAGE_URL}${this.restaurant.slug}/${sizedFileName}')`
				}
			} else {
				return {
					height: "60px"
				}
			}
		},
		cost() {
			return this.item.calcedPrice || this.item.price
		},
		showRequiresFoodDisclaimer() {
			return (
				this.item.contains_alcohol &&
				(this.$store.getters.cartHasOnlyAlcohol ||
					this.$store.itemsInCartCount == 0) &&
				this.foodItemSuggestions.length > 0
			)
		},
		foodItemSuggestions() {
			let suggestions = this.$store.state.menu.filter((item) => {
				const hasRequiredMetas =
					item.metas &&
					item.metas.length > 0 &&
					item.metas.some((meta) => meta.required)
				return !item.contains_alcohol && !hasRequiredMetas
			})
			return suggestions.length > 4
				? suggestions.splice(0, 4)
				: suggestions
		},
		suggestedItemSelected() {
			return this.$store.state.suggestedItemSelected
		},
		showComments() {
			return (
				this.item.contains_alcohol == false &&
				this.item.comments_enabled &&
				this.$store.state.business.type !== BusinessType.AIRPORT
			)
		},
		showSelectedDateTag() {
			return (
				this.$store.state.business &&
				this.$store.state.business.type == BusinessType.RESIDENTIAL &&
				!this.$route.meta.isEvent
			)
		},
		selectedDateDay() {
			return this.$store.state.deliveryDate &&
				this.$store.state.deliveryDate.date
				? moment(
						this.$store.state.deliveryDate.date,
						"YYYY-MM-DD"
				  ).format("dddd")
				: ""
		},
		productMenu() {
			const menus = this.$store.getters.menusHash
			if (
				this.item &&
				menus &&
				menus[this.item.menu_id] &&
				menus[this.item.menu_id].menu
			) {
				return menus[this.item.menu_id].menu
			}
			return null
		},
		productMenuName() {
			if (this.productMenu) {
				return this.productMenu.name
			}
			return ""
		},
		metas() {
			if (this.item && this.item.metas && this.item.metas.length > 0) {
				const isInPreorderMode = this.$store.state.deliveryDate != null
				let ignoreStockStatus = false
				if (isInPreorderMode) {
					const todayDow = momentUtils.todaysDow()
					const tomorrowDow = todayDow == 6 ? 0 : 1
					const preorderDate = moment(
						this.$store.state.deliveryDate.date,
						"YYYY-MM-DD"
					)
					const preorderDow =
						momentUtils.normalizeDayOfWeek(preorderDate)
					const menuInStock = this.productMenu
						? this.productMenu.stock_status == 1
						: false
					if (preorderDow == todayDow || preorderDow == tomorrowDow) {
						const menuId = this.item.menu_id
						if (
							menuId == undefined ||
							!this.$store.state.menuSchedules[menuId]
						) {
							this.bugsnag.leaveBreadcrumb(
								"Missing menu schedule",
								{
									menuId: menuId,
									schedules: this.$store.state.menuSchedules,
									item: this.item
								}
							)
						}
						const schedules =
							this.$store.state.menuSchedules[menuId]
						const isLapsedMenu =
							Object.values(schedules)
								.flat()
								.filter(
									(schedule) =>
										schedule.rangesFrom == preorderDow
								).length > 1
						ignoreStockStatus =
							isLapsedMenu || preorderDow == todayDow
								? !menuInStock
								: undefined
					} else {
						ignoreStockStatus = true
					}
				}

				return this.item.metas
					.filter((meta) => {
						if (ignoreStockStatus) {
							return true
						} else {
							return (
								meta.values &&
								meta.values.length > 0 &&
								meta.values.some(
									(value) => value.stock_status == 1
								)
							)
						}
					})
					.slice()
					.map((meta) => {
						if (!ignoreStockStatus) {
							meta.values = meta.values.filter(
								(value) => value.stock_status == 1
							)
						}
						return meta
					})
					.sort((a, b) => {
						return b.required - a.required
					})
			} else {
				return null
			}
		}
	},
	methods: {
		async open(options) {
			this.waitingToOpen = false
			this.commentsOpen = this.item.comments != undefined
			if (!this.$route.query.editing && (!options || !options.editing)) {
				const gotItem = await this.$store.dispatch("getItem", {
					id: this.$route.query.item_id,
					menuId: this.$route.query.menu_id
				})
				if (!gotItem) {
					this.$router.replace({ query: null })
					return
				}
			}
			$(this.$refs.modal).modal("show")
		},
		close() {
			this.$refs.itemContent.scrollTo({
				top: 0
			})
			$(this.$refs.modal).modal("hide")
		},
		optionChanged: function (event) {
			this.$store.dispatch("setCurrentItemOption", {
				option: {
					checked: event.checked,
					name: event.option.name,
					price: event.option.price,
					meta_id: event.option.meta_id,
					id: event.option.id
				},
				key: event.meta.key,
				maxAllowedValues: event.meta.max_allowed_values
			})
			this.$store.dispatch("sendMixpanelEvent", {
				msg: "Product Meta Clicked",
				data: {
					id: event.option.meta_id
				}
			})
		},
		setComments(event) {
			this.$store.dispatch("setCurrentItemComments", event.target.value)
		},
		scrollToRequiredMeta(id) {
			const ref = this.$refs["meta-" + id]
			if (ref && ref[0]) {
				ref[0].scrollIntoView({ block: "start", behavior: "smooth" })
				this.invalidMetaId = id
				setTimeout(() => {
					this.invalidMetaId = null
				}, 2000)
			}
			this.$store.dispatch("sendMixpanelEvent", {
				msg: "Required Meta Missing",
				data: {
					id: id
				}
			})
		},
		toggleInstructions() {
			if (!this.commentsOpen) {
				this.waitingToOpen = true
			}
			this.commentsOpen = !this.commentsOpen
		},
		commentsOpened() {
			setTimeout(() => {
				this.waitingToOpen = false
				this.$refs.itemContent.scroll({
					top: this.$refs.itemContent.scrollHeight,
					behavior: "smooth"
				})
			}, 0)
		},
		foodItemSuggestionSelected(item) {
			if (this.suggestedItemSelected.id == item.id) {
				this.$store.dispatch("selectSuggestedItem", {})
			} else {
				this.$store.dispatch("selectSuggestedItem", item)
			}
		},
		async confirmClear(promise) {
			this.$store.dispatch("sendMixpanelEvent", {
				msg: "Clear Cart Confirmation"
			})
			const confirmed = await this.$refs.confirmationModal.open({
				title: "",
				body: "Adding products from this menu will clear your current order, are you sure you want continue?",
				cancel: "Cancel",
				confirmation: "Continue"
			})
			if (confirmed) {
				this.$store.dispatch("sendMixpanelEvent", {
					msg: "Clear Cart Selected"
				})
			} else {
				this.$store.dispatch("sendMixpanelEvent", {
					msg: "Keep Cart Selected"
				})
			}
			promise.resolve(confirmed)
		}
	},
	mounted: function () {
		$(this.$refs.modal).on("hide.bs.modal", () => {
			this.$router.replace({ query: null })
		})
	},
	beforeDestroy: function () {
		$(this.$refs.modal).modal("hide")
	}
}
</script>

<style scoped>
@keyframes bounce {
	0% {
		padding-left: 0px;
	}
	25%,
	75% {
		padding-left: 15px;
	}
	50% {
		padding-left: 10px;
	}
	100% {
		padding-left: 10px;
	}
}

.modal-close {
	z-index: 1000;
	position: absolute;
	top: 15px;
	right: 15px;
}

.modal-close i {
	font-size: 25px;
	color: var(--gR-white);
}

.dark-modal-close.modal-close i {
	color: var(--gR-white);
	background-color: var(--gR-teal-70);
	padding: 4px;
	height: 2rem;
	width: 2rem;
	border-radius: 50%;
}

.modal-dialog {
	height: calc(100% - 56px);
}

.modal-content {
	border-radius: 5px;
	max-height: 100%;
	overflow: hidden;
}

.item-content {
	overflow-y: auto;
	max-height: calc(100% - 76px);
}

.panel {
	overflow: visible;
}

.button-white {
	margin-top: 30px;
	font-size: 14px;
	width: 225px;
}

.item-modal-choose {
	font-weight: 700;
	font-size: 16px;
	margin-top: 23px;
	margin-bottom: 12px;
	transition: all 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275);
	border: 0 solid transparent;
	color: var(--gR-base-90);
	display: flex;
	justify-content: space-between;
	align-items: baseline;
}

.item-modal-subtitle {
	font-size: 12px;
	color: var(--sk-grey2);
	margin-top: -8px;
}

.item-image {
	height: 333px;
	flex-shrink: 0;
	background-position: center center;
	position: relative;
}

.invalid .item-modal-choose {
	border: 2px solid var(--gR-teal-70);
	border-radius: 10px;
	padding: 0 10px;
	animation: bounce 0.5s;
}

.item-modal-footer {
	padding: 10px 30px 50px 30px;
	border-top: none;
}

.item-modal-title {
	display: flex;
	justify-content: space-between;
	font-weight: 700;
	font-size: 18px;
}

@media only screen and (max-width: 700px) {
	.item-modal-title {
		font-size: 18px;
	}
}

.item-modal-title,
.item-modal-title span {
	color: var(--gR-base-90);
}

.item-modal-title span {
	margin-left: 20px;
}

.item-modal-desc {
	font-size: 14px;
	margin-bottom: 23px;
	color: var(--sk-grey3);
	line-height: 18px;
	word-break: break-word;
}

.item-option-required {
	font-size: 12px;
	color: var(--gR-teal-70);
}

textarea {
	min-height: 85px;
}

.selected-date-tag {
	position: absolute;
	top: 15px;
	left: 20px;
	background: var(--gR-base-90);
	color: var(--gR-white);
	border-radius: 5px;
	font-size: 12px;
	padding: 3px 5px;
}

.selected-date-tag b {
	color: var(--gR-white);
}

@media (max-width: 500px) {
	.modal-dialog {
		margin: 0;
		height: 100%;
	}

	.modal-content {
		max-height: 100%;
		height: 100%;
		border-radius: 0;
	}

	.item-content {
		height: calc(100% - 76px);
	}

	.modal-close {
		position: fixed;
	}
}
</style>